///<amd-module name='AjaxHelper'/>

import { EndpointResponse, Endpoint } from "../Interfaces/Interfaces";
import { cancel } from "./ExecutionStatus";

const global: Window = window;
const { selfHelp: { resources } } = global;

/**
 * function makes a jQuery ajax HEAD request
 *
 * @param {string} uri the URI to which the ajax request should be made
 * @returns {Promise<any>} promise containing response data
 */
export function headRequest(uri: string): Promise<any> {
    if (cancel) {
        return;
    }
    return new Promise((resolve, reject) => {
        const xhr: XMLHttpRequest = new XMLHttpRequest();
        xhr.onreadystatechange = function() {
            if (this.readyState === 4) {
                xhr.status === 200 ? resolve(xhr) : reject(xhr);
            }
        };
        xhr.open("HEAD", uri, true);
        xhr.send();
    });
}

/**
 * function makes a GET request
 *
 * @param {string} uri the URI to which the ajax request should be made
 * @returns {Promise<any>} promise containing response data
 */
export function getRequest(uri: string): Promise<any> {
    if (cancel) {
        return;
    }
    return new Promise((resolve, reject) => {
        const xhr: XMLHttpRequest = new XMLHttpRequest();
        xhr.onreadystatechange = function() {
            if (this.readyState === 4) {
                xhr.status === 200 ? resolve(xhr) : reject(xhr);
            }
        };
        xhr.open("GET", uri, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.send();
    });
}

/**
 * function makes a GET request
 *
 * @param {string} uri the URI to which the ajax request should be made
 * @returns {Promise<any>} promise containing response data
 */
export function postRequest(uri: string, withAuthentication: boolean, data: any): Promise<unknown> {
    return new Promise((resolve, reject) => {
        let token: string;
        const { cookie } = document;
        const stringified = JSON.stringify(data);

        const xhr: XMLHttpRequest = new XMLHttpRequest();
        xhr.onreadystatechange = function() {
            if (this.readyState === 4) {
                xhr.status === 200 ? resolve(xhr) : reject(xhr);
            }
        };

        xhr.open("POST", uri);
        xhr.setRequestHeader("Content-Type", "application/json");

        // used for Telemetry
        if (cookie) { token = cookie.split("oauth-token=")[1]; }
        if (withAuthentication && token) { xhr.setRequestHeader("Authorization", token); }

        xhr.send(stringified);
    });
}

/**
 * Constructs EndpointResponse object from xhr res object
 *
 * @param {Endpoint} endpoint - endpoint the response object is being constructed for
 * @param {any} res - the response data from the ping
 * @param {boolean} clientSideRequest - indicates whether response is from client or sever side request
 */
function handleResponse(endpoint: Endpoint, res: any, clientSideRequest: boolean): EndpointResponse {
    if (res.status < 500 && !clientSideRequest) {
        let serverResponse = res.responseText;
        serverResponse = JSON.parse(serverResponse);
        return Object.assign(endpoint, { status: serverResponse.StatusCode, statusText: serverResponse.ReasonPhrase });
    }
    return Object.assign(endpoint, { status: res.status, statusText: res.statusText });
}

/**
 * Helper function for pinging an Endpoint[] and consolidating server responses.
 * Returns an array of endpoints with their associated responses.
 *
 * @param {Endpoint[]} endpoints - an array of endpoints to ping
 * @returns {Promise<EndpointResponse[]>} promise which resolves to an EndpointResponses[]
 */
export async function pingEndpoints(endpoints: Endpoint[], clientSideRequest: boolean): Promise<EndpointResponse[]> {
    if (cancel) {
        return;
    }

    const responses: EndpointResponse[] = [];
    const requestType = clientSideRequest ? headRequest : getRequest;

    const promises = endpoints.map((endpoint) => {
        if (cancel) {
            return;
        }
        if (!endpoint.uri) {
            responses.push({ ...endpoint, status: 0, statusText: resources.Endpoint_unavailable });
            return;
        }
        const requestUri: string = clientSideRequest ? endpoint.uri : `./SelfHelp/AsyncRequest?uri=${endpoint.uri}`;
        return requestType(requestUri)
            .then((res) => {
                const result = handleResponse(endpoint, res, clientSideRequest);
                responses.push(result);
            })
            .catch((err) => {
                const result = handleResponse(endpoint, err, clientSideRequest);
                responses.push(result);
            });
    });

    await Promise.all(promises);
    return responses;
}
